/**
 * Wave 2.5: Operator Declarations Card
 * 
 * Displays and allows editing of operator declarations for SORA GRC calculation.
 * - gathering_of_people: Forces GRC-7 (highest risk)
 * - controlled_ground_area: Allows GRC-1-2 (lowest risk)
 */
import { useState, useEffect } from "react";
import { 
  Users, ShieldCheck, AlertTriangle, ChevronDown, ChevronUp, 
  Info, Pencil, Save, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";
import { toast } from "sonner";
import api from "../../lib/api";

export const OperatorDeclarationsCard = ({
  mission,
  onUpdate,
  language = 'cs',
  t = {},
  forceExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // "Doplnit" from a provisional-iGRC reason (see MissionDetail.js's navigateToDetailCard) — force this
  // card open and into edit mode so the required reason field is immediately visible/editable.
  useEffect(() => {
    if (forceExpanded) {
      setExpanded(true);
      setEditing(true);
    }
  }, [forceExpanded]);
  
  // Local state for editing
  const [gatheringOfPeople, setGatheringOfPeople] = useState(
    mission?.operator_declarations?.gathering_of_people || false
  );
  const [controlledGroundArea, setControlledGroundArea] = useState(
    mission?.operator_declarations?.controlled_ground_area || false
  );
  const [declarationReason, setDeclarationReason] = useState(
    mission?.operator_declarations?.declaration_reason || ""
  );

  // Translations
  const translations = {
    title: language === 'cs' ? 'Deklarace operačního prostoru' : 'Operational Space Declarations',
    subtitle: language === 'cs' 
      ? 'Operátor může deklarovat specifické podmínky ovlivňující GRC' 
      : 'Operator can declare specific conditions affecting GRC',
    gatheringLabel: language === 'cs' ? 'Shromáždění osob' : 'Gathering of people',
    gatheringDescription: language === 'cs' 
      ? 'Operace probíhá v oblasti se shromážděním osob (koncert, sportovní akce, demonstrace apod.)' 
      : 'Operation takes place in area with gathering of people (concert, sports event, demonstration, etc.)',
    gatheringWarning: language === 'cs'
      ? 'Zvyšuje riziko na nejvyšší možnou úroveň (GRC-7, GRC-8, nebo mimo rozsah SORA — podle rozměru a rychlosti dronu)'
      : 'Increases risk to the highest possible level (GRC-7, GRC-8, or out of SORA scope, depending on drone dimension/speed)',
    controlledLabel: language === 'cs' ? 'Kontrolovaná pozemní oblast' : 'Controlled ground area',
    controlledDescription: language === 'cs' 
      ? 'Operátor kontroluje přístup do letové oblasti (staveniště, uzavřený areál, vojenský prostor apod.)' 
      : 'Operator controls access to flight area (construction site, closed facility, military area, etc.)',
    controlledBenefit: language === 'cs' 
      ? 'Aktivace umožňuje GRC-1-2 (nejnižší riziko)' 
      : 'Activation allows GRC-1-2 (lowest risk)',
    reasonLabel: language === 'cs' ? 'Odůvodnění deklarace' : 'Declaration reason',
    reasonPlaceholder: language === 'cs'
      ? 'Popište konkrétní okolnosti...'
      : 'Describe specific circumstances...',
    reasonRequiredForCga: language === 'cs'
      ? 'Odůvodnění je povinné, pokud je aktivní kontrolovaná pozemní oblast — bez něj se deklarace nezohlední ve výpočtu.'
      : 'A reason is required while controlled ground area is active — without it, the declaration is not honored in the calculation.',
    edit: language === 'cs' ? 'Upravit' : 'Edit',
    save: language === 'cs' ? 'Uložit' : 'Save',
    cancel: language === 'cs' ? 'Zrušit' : 'Cancel',
    saving: language === 'cs' ? 'Ukládám...' : 'Saving...',
    saved: language === 'cs' ? 'Deklarace uložena' : 'Declaration saved',
    error: language === 'cs' ? 'Chyba při ukládání' : 'Error saving',
    noDeclarations: language === 'cs' 
      ? 'Žádné deklarace nejsou aktivní' 
      : 'No declarations are active',
    declaredAt: language === 'cs' ? 'Deklarováno' : 'Declared',
    ...t
  };

  // CGA is only honored by the backend (SoraProvisionalReasonCodes.Codes.CgaMissingReason) when a
  // non-empty reason is declared alongside it — mirror that gate here so the save button itself
  // never submits a CGA declaration that would silently be ignored.
  const reasonRequired = controlledGroundArea && !declarationReason.trim();

  const handleSave = async () => {
    if (!mission?.id || reasonRequired) return;

    setSaving(true);
    try {
      const payload = {
        gathering_of_people: gatheringOfPeople,
        controlled_ground_area: controlledGroundArea,
        declaration_reason: declarationReason || null,
        declared_at: new Date().toISOString()
      };
      
      await api.put(`/missions/${mission.id}/operator_declarations`, payload);
      
      toast.success(translations.saved);
      setEditing(false);
      
      // Trigger parent refresh
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Failed to save declarations:", error);
      toast.error(translations.error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setGatheringOfPeople(mission?.operator_declarations?.gathering_of_people || false);
    setControlledGroundArea(mission?.operator_declarations?.controlled_ground_area || false);
    setDeclarationReason(mission?.operator_declarations?.declaration_reason || "");
    setEditing(false);
  };

  // Determine current state
  const hasDeclarations = gatheringOfPeople || controlledGroundArea;
  const declaredAt = mission?.operator_declarations?.declared_at;

  return (
    <Card 
      className="overflow-hidden"
      data-testid="operator-declarations-card"
    >
      <CardHeader 
        className="cursor-pointer hover:bg-[var(--bg-elevated)] transition-colors py-3 px-4"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[var(--text-muted)]" />
            <CardTitle className="text-base font-medium">
              {translations.title}
            </CardTitle>
            {hasDeclarations && (
              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                gatheringOfPeople 
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              }`}>
                {gatheringOfPeople
                  ? (language === 'cs' ? 'Zvýšené riziko' : 'Increased risk')
                  : (language === 'cs' ? 'GRC sníženo' : 'GRC reduced')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {expanded && !editing && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(true);
                }}
              >
                <Pencil className="h-4 w-4 mr-1" />
                {translations.edit}
              </Button>
            )}
            {expanded ? (
              <ChevronUp className="h-5 w-5 text-[var(--text-muted)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--text-muted)]" />
            )}
          </div>
        </div>
        {!expanded && (
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {hasDeclarations 
              ? (gatheringOfPeople 
                  ? translations.gatheringLabel 
                  : translations.controlledLabel)
              : translations.noDeclarations}
          </p>
        )}
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 px-4 pb-4 space-y-4">
          {/* Description */}
          <p className="text-sm text-[var(--text-muted)]">
            {translations.subtitle}
          </p>

          {/* Gathering of People */}
          <div className={`p-4 rounded-lg border ${
            gatheringOfPeople 
              ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30' 
              : 'border-[var(--border-default)] bg-[var(--bg-elevated)]'
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Users className={`h-5 w-5 ${gatheringOfPeople ? 'text-red-600' : 'text-[var(--text-muted)]'}`} />
                  <Label className="font-medium">{translations.gatheringLabel}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-[var(--text-muted)] cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{translations.gatheringDescription}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-sm text-[var(--text-muted)] mt-1 ml-7">
                  {translations.gatheringDescription}
                </p>
                {gatheringOfPeople && (
                  <div className="flex items-center gap-1 mt-2 ml-7">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium text-red-600">
                      {translations.gatheringWarning}
                    </span>
                  </div>
                )}
              </div>
              <Switch
                checked={gatheringOfPeople}
                onCheckedChange={setGatheringOfPeople}
                disabled={!editing}
                data-testid="gathering-switch"
              />
            </div>
          </div>

          {/* Controlled Ground Area */}
          <div className={`p-4 rounded-lg border ${
            controlledGroundArea 
              ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/30' 
              : 'border-[var(--border-default)] bg-[var(--bg-elevated)]'
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <ShieldCheck className={`h-5 w-5 ${controlledGroundArea ? 'text-green-600' : 'text-[var(--text-muted)]'}`} />
                  <Label className="font-medium">{translations.controlledLabel}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-[var(--text-muted)] cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{translations.controlledDescription}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-sm text-[var(--text-muted)] mt-1 ml-7">
                  {translations.controlledDescription}
                </p>
                {controlledGroundArea && (
                  <div className="flex items-center gap-1 mt-2 ml-7">
                    <ShieldCheck className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-600">
                      {translations.controlledBenefit}
                    </span>
                  </div>
                )}
              </div>
              <Switch
                checked={controlledGroundArea}
                onCheckedChange={setControlledGroundArea}
                disabled={!editing}
                data-testid="controlled-switch"
              />
            </div>
          </div>

          {/* Reason textarea (only when editing or has content) */}
          {(editing || declarationReason) && (
            <div className="space-y-2">
              <Label>
                {translations.reasonLabel}
                {editing && controlledGroundArea && <span className="text-red-600 ml-0.5">*</span>}
              </Label>
              <Textarea
                value={declarationReason}
                onChange={(e) => setDeclarationReason(e.target.value)}
                placeholder={translations.reasonPlaceholder}
                disabled={!editing}
                rows={3}
                aria-invalid={editing && reasonRequired}
                data-testid="declaration-reason"
              />
              {editing && reasonRequired && (
                <p className="text-xs text-red-600" data-testid="declaration-reason-required">
                  {translations.reasonRequiredForCga}
                </p>
              )}
            </div>
          )}

          {/* Declared at timestamp */}
          {declaredAt && !editing && (
            <p className="text-xs text-[var(--text-muted)]">
              {translations.declaredAt}: {new Date(declaredAt).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-GB')}
            </p>
          )}

          {/* Action buttons when editing */}
          {editing && (
            <div className="flex justify-end gap-2 pt-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleCancel}
                disabled={saving}
              >
                <X className="h-4 w-4 mr-1" />
                {translations.cancel}
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving || reasonRequired}
              >
                <Save className="h-4 w-4 mr-1" />
                {saving ? translations.saving : translations.save}
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
